import { PageContainer } from '@/components/PageContainer';
import { TablePagination } from '@/components/TablePagination';
import { trpc } from '@/lib/trpc';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Text } from '@televet/kibble-ui/Text';
import { Flex } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { BlueprintListToolbar } from './-components/BlueprintListToolbar';
import { BlueprintTable } from './-components/BlueprintTable';
import { CloneBlueprintModal } from './-components/CloneBlueprintModal';
import { DeleteBlueprintDialog } from './-components/DeleteBlueprintDialog';
import { DeletedBlueprints } from './-components/DeletedBlueprints';
import { NewBlueprintModal } from './-components/NewBlueprintModal';
import { QueryErrorAlert } from './-components/QueryErrorAlert';
import { useBlueprintListing } from './-components/useBlueprintListing';
import type { BlueprintListRow } from './-lib/types';

export const Route = createFileRoute('/_authenticated/_testing-tools/blueprints/')({
  beforeLoad: () => ({ pageTitle: 'Blueprints' }),
  component: LabBlueprintsPage,
});

const emptyMessage = (isFiltered: boolean): string =>
  isFiltered
    ? 'No blueprints match these filters.'
    : 'No blueprints yet. Add one with New blueprint, then use Make default on it to make it a default.';

function LabBlueprintsPage() {
  const [cloneSource, setCloneSource] = useState<BlueprintListRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BlueprintListRow | null>(null);
  const [isNewOpen, setIsNewOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery(trpc.labDataBlueprint.list.queryOptions());
  const listing = useBlueprintListing(data);
  const { rows, currentPage, totalPages } = listing;

  return (
    <PageContainer>
      <Flex direction="column" gap={4} w="100%">
        <BlueprintListToolbar
          search={listing.search}
          onSearch={listing.changeSearch}
          typeFilter={listing.typeFilter}
          onTypeFilter={listing.changeTypeFilter}
          onNew={() => setIsNewOpen(true)}
        />
        {isLoading && <Spinner label="Loading blueprints" />}
        {error && (
          <QueryErrorAlert
            title="Could not load blueprints"
            message={error.message}
            onRetry={() => void refetch()}
            data-testid="lab-blueprint-list-load-error"
          />
        )}
        {data && rows.length > 0 && (
          <>
            <BlueprintTable
              rows={listing.pageRows}
              sort={listing.sort}
              onSort={listing.changeSort}
              onClone={setCloneSource}
              onDelete={setDeleteTarget}
            />
            <TablePagination
              page={currentPage}
              totalPages={totalPages}
              hasPrev={currentPage > 1}
              hasNext={currentPage < totalPages}
              onPageChange={listing.setPage}
              summary={`${rows.length} blueprint${rows.length === 1 ? '' : 's'}`}
              data-testid-prefix="lab-blueprint-list"
            />
          </>
        )}
        {data && rows.length === 0 && (
          <Flex justify="center" p={8}>
            <Text variant="subtle" data-testid="lab-blueprint-list-empty">
              {emptyMessage(listing.isFiltered)}
            </Text>
          </Flex>
        )}
        <DeletedBlueprints />
      </Flex>
      {isNewOpen && <NewBlueprintModal onClose={() => setIsNewOpen(false)} />}
      {cloneSource && (
        <CloneBlueprintModal source={cloneSource} onClose={() => setCloneSource(null)} />
      )}
      <DeleteBlueprintDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </PageContainer>
  );
}
